/**
 * LGPD Block B — consent-gated script loader.
 *
 * The ONLY sanctioned path for injecting third-party analytics or marketing
 * scripts into the app. Google Analytics, GTM, Meta Pixel, Sentry and any
 * future tracking/marketing script MUST be loaded through
 * ``loadAnalyticsScript`` / ``loadMarketingScript`` — never via a raw
 * ``<script>`` tag in ``index.html`` or a manual ``document.createElement``.
 * That rule is what keeps the consent banner honest: no opt-in, no script.
 *
 * No actual GA/Meta/Sentry integration exists in this block; this is the
 * foundation only.
 */

import { hasAnalyticsConsent, hasMarketingConsent } from './consentManager'

export interface LoadScriptOptions {
  /** ``async`` attribute (default true). */
  async?: boolean
  /** ``defer`` attribute (default false). */
  defer?: boolean
  /** Extra attributes, e.g. ``{ 'data-domain': 'rager.app' }``. */
  attributes?: Record<string, string>
  onLoad?: () => void
  onError?: () => void
}

/** Marks loader-injected tags so duplicates are detectable across callers. */
const SCRIPT_ATTR = 'data-rager-consent-script'

/** True when the user has explicitly opted into the analytics category. */
export function canLoadAnalytics(): boolean {
  return hasAnalyticsConsent()
}

/** True when the user has explicitly opted into the marketing category. */
export function canLoadMarketing(): boolean {
  return hasMarketingConsent()
}

function isAllowedSrc(src: string): boolean {
  // Only https URLs or same-origin absolute paths. Anything else (http:,
  // protocol-relative, javascript:, data:) is refused outright.
  if (src.startsWith('/') && !src.startsWith('//')) return true
  try {
    return new URL(src).protocol === 'https:'
  } catch {
    return false
  }
}

function injectScript(name: string, src: string, options: LoadScriptOptions): boolean {
  if (typeof document === 'undefined') return false
  if (!isAllowedSrc(src)) {
    console.warn(`[consent] refused script "${name}": src must be https or same-origin`)
    return false
  }
  // Dedup by name: a script that was already injected (this call or a
  // previous page lifecycle hook) is never added twice.
  if (document.querySelector(`script[${SCRIPT_ATTR}="${CSS.escape(name)}"]`)) {
    return false
  }
  const script = document.createElement('script')
  script.src = src
  script.async = options.async ?? true
  script.defer = options.defer ?? false
  script.setAttribute(SCRIPT_ATTR, name)
  if (options.attributes) {
    for (const [key, value] of Object.entries(options.attributes)) {
      script.setAttribute(key, value)
    }
  }
  if (options.onLoad) script.addEventListener('load', options.onLoad)
  if (options.onError) script.addEventListener('error', options.onError)
  document.head.appendChild(script)
  return true
}

/**
 * Inject an analytics-category script (future GA / product analytics).
 * Refuses — returning ``false`` and loading NOTHING — unless the user has a
 * stored, current, explicit analytics opt-in. Safe to call optimistically.
 */
export function loadAnalyticsScript(
  name: string,
  src: string,
  options: LoadScriptOptions = {},
): boolean {
  if (!canLoadAnalytics()) return false
  return injectScript(name, src, options)
}

/**
 * Inject a marketing-category script (future Meta Pixel / ad tools).
 * Refuses — returning ``false`` and loading NOTHING — unless the user has a
 * stored, current, explicit marketing opt-in. Safe to call optimistically.
 */
export function loadMarketingScript(
  name: string,
  src: string,
  options: LoadScriptOptions = {},
): boolean {
  if (!canLoadMarketing()) return false
  return injectScript(name, src, options)
}
